import _ from 'lodash';
import moment from 'moment-timezone';
import settings from 'settings';
import { ROSTERBUILDER_TIMELINE as types } from 'actions/actionTypes';
import { rosterBuilderToTimeline, TIMELINE_DAYS_TO_SHOW, TIMELINE_START_DATE } from 'selectors/timeline';
import { djangoDurationToMoment } from 'library/time';
import { message } from 'antd';

const { APP_CONFIG } = settings;

const initialState = {
  rosterBuilder: {
    name: '',
    id: null
  },
  groups: [{
    id: 0,
    title: 'New User'
  }],
  items: [],
  selected: [],
  inFlight: false,

  assignableUsers: [],
  visible: false,
  rosterEventDetail: {
    insertTime: null,
    insertGroupId: null
  },
  loading: false
};

const arrayToObject = array => array.reduce((acc, item) => {
  const newObj = { ...acc, [item.id]: item };
  return newObj;
}, {});

// NOTE: Does not return new groups. Works in place on items and groups params
const removeEmptyGroups = (items, groups) => {
  let index = 0;
  const indexToRemove = [];
  let nextGroupId = 0;
  while (index < groups.length) {
    const group = groups[index];
    const itemsInGroup = _.filter(items, i => i.group === group.id);
    if (itemsInGroup.length === 0) {
      indexToRemove.push(index);
    }
    else {
      if (group.id !== nextGroupId) {
        for (let i = 0; i < itemsInGroup.length; i++) {
          itemsInGroup[i].group = nextGroupId;
        }
        group.id = nextGroupId;
      }
      nextGroupId += 1;
    }
    index += 1;
  }
  for (let i = indexToRemove.length - 1; i >= 0; i -= 1) {
    groups.splice(indexToRemove[i], 1);
  }
};

const resetGroups = (items, groups) => {
  removeEmptyGroups(items, groups);
  groups.map(group => ({
    ...group,
    title: `User ${group.id + 1}`
  }));
  const maxGroupId = Math.max(...groups.map(g => g.id), -1);
  groups.push({
    id: maxGroupId + 1,
    title: 'New User'
  });
};

export const rosterBuilderTimeline = (state = initialState, action) => {
  switch (action.type) {
  case types.REQUEST_IN_FLIGHT: {
    return {
      ...state,
      inFlight: true
    };
  }

  case types.SAVE_ROSTERBUILDER_SUCCESS: {
    return { ...state, inFlight: false };
  }

  case types.SAVE_ROSTERBUILDER_FAILURE: {
    return { ...state, inFlight: false };
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.DELETE_ENTIRE_GROUP: {
    const { groupId } = action;

    const items = _.cloneDeep(state.items)
      .filter(i => i.group !== groupId);
    const groups = _.cloneDeep(state.groups);

    resetGroups(items, groups);
    return {
 ...state,
items,
      groups
};
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.NEW_ROSTERBUILDER: {
    return initialState;
  }

  case types.LOADED_ROSTERBUILDER_SUCCESS: {
    const { items, groups, rosterBuilder } = rosterBuilderToTimeline(action.data);
    const users = _.cloneDeep(state.assignableUsers);
    resetGroups(items, groups);
    const groupMap = new Map(groups.map(group => [group.id, group]));
    const userMap = new Map(users.map(user => [user.uid, user]));

    items.forEach((item) => {
      const groupVal = groupMap.get(item.group);
      if (groupVal) {
        const userVal = userMap.get(item.user);
      if (userVal) {
        groupVal.assignedUser = userVal;
      }
      }
    });

    return {
 ...state,
items,
      groups,
      rosterBuilder,
      inFlight: false
};
  }

  case types.LOADED_ROSTERBUILDER_FAILURE: {
    return { ...state, inFlight: false };
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.DELETE_TIMELINE_ACTIVITY: {
    // if there is currently a selected item, then delete it from state.
    // const items = _.cloneDeep(state.items);

    const newItems = state.items.filter(i => !state.selected.includes(i.id));
    const groups = _.cloneDeep(state.groups);

    resetGroups(newItems, groups);
    return {
 ...state,
items: newItems,
      groups
};
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.SELECT_TIMELINE_ACTIVITIES: {
    return { ...state, selected: action.ids };
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.ADD_ACTIVITY_TO_TIMELINE: {
    const {
      group, activity, time, createActivityCount, hoursBetween
    } = action;
    const items = _.cloneDeep(state.items);
    const groups = _.cloneDeep(state.groups);
    const assignedGroupId = Number(group);

    // Find the highest existing id and increment for a new id.
    const existingIds = items.map(i => i.id);
    const newItemId = Math.max(0, ...existingIds) + 1;
    // if multiple activities were specified.
    for (let i = 0; i < createActivityCount; i++) {
      const startTime = moment(time)
        .add(i * hoursBetween, 'hours');
      const activityDuration = djangoDurationToMoment(activity.duration);

      const endTime = moment(startTime)
        .add(activityDuration);

      const newItem = {
        id: newItemId + i,
        group: assignedGroupId,
        title: activity.name,
        start_time: startTime,
        end_time: endTime,
        itemProps: { style: { backgroundColor: activity.color } },
        color: activity.color,
        activity: { ...activity },
        default_notifications: activity.default_notifications,
        is_new: true
      };
      items.push(newItem);
    }
    resetGroups(items, groups);
    return {
 ...state,
items,
      groups
};
  }

  case types.UPDATE_ACTIVITIES: {
    const { items } = action;
    const currentItems = _.cloneDeep(state.items);

    items.forEach((newItem) => {
      const itemIndex = currentItems.findIndex(item => item.id === newItem.id);
      if (itemIndex !== -1) {
        currentItems[itemIndex] = newItem;
        currentItems[itemIndex].color = newItem.activity.color;
        currentItems[itemIndex].title = newItem.activity.name;
      }
    });
    return { ...state, items: currentItems };
  }

  case types.RESIZE_ACTIVITIES: {
    const { changes, items } = action;
    const currentItems = _.cloneDeep(state.items);

    const lookupItems = arrayToObject(currentItems); // setup the lookup table

    // loop through the items selected as filter returns
    // the selectedItem and not the state items
    // Avoiding looping through all state items
    const selectedItems = [];
    for (let i = 0; i < items.length; i += 1) {
      selectedItems.push(lookupItems[items[i].key]);
    }

    // update the selected items
    for (let i = 0; i < selectedItems.length; i += 1) {
      const currentItem = selectedItems[i];
      if (changes.isStartTimeChange) {
        const maxMinsAddable = currentItem.end_time.diff(currentItem.start_time, 'minutes') - APP_CONFIG.minActivityDurationMinutes;
        currentItem.start_time.add(Math.min(maxMinsAddable, changes.timeDelta), 'minutes');
      }
      else {
        const minMinsAddable = -1 * (currentItem.end_time.diff(currentItem.start_time, 'minutes') - APP_CONFIG.minActivityDurationMinutes);
        currentItem.end_time.add(Math.max(minMinsAddable, changes.timeDelta), 'minutes');
      }
      // add delta to duration to keep updated
      currentItem.activity.duration += changes.timeDelta;
    }

    return { ...state, items: currentItems };
  }

  case types.MOVE_ACTIVITIES: {
    const { changes, items } = action;

    const timelineStartDateBoundary = moment(TIMELINE_START_DATE);
    const timelineEndDateBoundary = moment(timelineStartDateBoundary)
      .add(TIMELINE_DAYS_TO_SHOW, 'day');
    const currentItems = _.cloneDeep(state.items);
    const currentGroups = _.cloneDeep(state.groups);

    let wasError = false;
    items.forEach((newItem) => {
      // Update time
      const currentItem = currentItems.filter(item => item.id === newItem.key)
        .pop();
      currentItem.start_time.add(changes.timeDelta, 'minutes'); // TODO: Validate inside boundries
      currentItem.end_time.add(changes.timeDelta, 'minutes'); // TODO: Validate inside boundries

      if (currentItem.start_time.valueOf() < timelineStartDateBoundary.valueOf()) {
        message.error('Activity moved before roster builder start time');
        wasError = true;
        return;
      }

      if (currentItem.end_time.valueOf() > timelineEndDateBoundary.valueOf()) {
        message.error('Activity moved after roster builder end time');
        wasError = true;
        return;
      }

      // Update Group
      const assignedGroupId = newItem.row;
      currentItem.group = assignedGroupId;
    });

    if (wasError) return state;
    resetGroups(currentItems, currentGroups);
    return {
 ...state,
items: currentItems,
      groups: currentGroups
};
  }

  // /////////////////////////////////////////////////////////////////////////
  case types.REORDER_GROUP: {
    const { toGroupId, fromGroupId } = action;

    if (toGroupId === fromGroupId || toGroupId === -1) {
      return state;
    }

    const items = _.cloneDeep(state.items);

    const minGroupId = Math.min(toGroupId, fromGroupId);
    const maxGroupId = Math.max(toGroupId, fromGroupId);
    const incr = Math.sign(fromGroupId - toGroupId);

    const new_items = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.group >= minGroupId && item.group <= maxGroupId) {
        if (item.group === fromGroupId) {
          item.group = toGroupId;
        }
        else {
          item.group += incr;
        }
      }
      new_items.push(item);
    }
    return { ...state, items: new_items };
  }

  case types.UPDATE_PINNED_ACTIVITIES: {
      return { ...state, rosterBuilderPinnedActivities: action.activities };
  }

  case types.SET_USERS: {
    const {
      assignableUsers,
    } = state;

    // Filter out empty if need be
      const filteredUsers = action.users
        .map(user => ({
          uid: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          title: `${user.last_name}, ${user.first_name}`,
          active: user.is_active && user.is_tenancy_active
        }));
      // Combine the pinned users with the new user list placing the pinned users at the top of the list
      let id = 1;
      const newGroups = assignableUsers.concat(filteredUsers)
        .map(g => ({
          ...g,
          id: id++
        }));

      return { ...state, assignableUsers: newGroups };
    }

    case types.LOADING_USERS_FAILED: {
      return initialState;
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.LOADING_USERS_IN_PROGRESS: {
      return { ...state, loading: true };
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.LOADED_USERS_ROSTERBUILDER_SUCCESS: {
      const { items, groups, rosterBuilder } = rosterBuilderToTimeline(action.activityRosterBuilder);
      const users = _.cloneDeep(state.assignableUsers);

      // Add the roster builder user to the user group
      for (let i = 0; i < groups.length; i++) {
        groups[i].assignedUser = users[i];
      }

      return {
 ...state,
items,
        groups,
        rosterBuilder,
        loading: false
};
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.REMOVE_ASSIGNED_USER: {
      const { placeHolderGroupId } = action;
      const groups = _.cloneDeep(state.groups);

      // Find the existing group id and remove the assigned user.
      const idx = groups.findIndex(g => g.id === placeHolderGroupId);
      groups[idx].assignedUser = null;

      return { ...state, groups };
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.ASSIGN_USER_TO_PLACEHOLDER: {
      const { placeHolderGroupId, user } = action;
      const groups = _.cloneDeep(state.groups);

      // Find the existing group id and assign the incoming user as the assigned.
      const idx = groups.findIndex(g => g.id === placeHolderGroupId);

      groups[idx].assignedUser = user;

      return { ...state, groups };
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.SWAP_USER_WITH_PLACEHOLDER: {
      const { toGroupId, fromGroupId } = action;
      const groups = _.cloneDeep(state.groups);
      const toGroupIdx = groups.findIndex(g => g.id === toGroupId);
      const fromGroupIdx = groups.findIndex(g => g.id === fromGroupId);
      const toAssignedUser = groups[toGroupIdx].assignedUser;
      const fromAssignedUser = groups[fromGroupIdx].assignedUser;
      groups[toGroupIdx].assignedUser = fromAssignedUser;
      groups[fromGroupIdx].assignedUser = toAssignedUser;

      return { ...state, groups };
    }

    // /////////////////////////////////////////////////////////////////////////
    case types.REORDER_ASSIGNED_USER: {
      const { toGroupId, fromGroupId } = action;

      if (toGroupId === fromGroupId || toGroupId === 0) {
        return state;
      }

      const groups = _.cloneDeep(state.groups);

      const fromGroupIdx = groups.findIndex(g => g.id === fromGroupId);
      const fromAssignedUser = groups[fromGroupIdx].assignedUser;

      const minGroupId = Math.min(toGroupId, fromGroupId);
      const maxGroupId = Math.max(toGroupId, fromGroupId);

      // Determine the direction, eg bubble up or bubble down
      // +1 is a bubble up, -1 is a bubble down. In reality,
      // this is modelled by reversing the affected array items
      const incr = Math.sign(toGroupId - fromGroupId);

      // filter the groups to the set that are being reordered, minus
      // any unassigned users in between
      let targetGroups = groups.filter((g) => {
        if (g.id >= minGroupId && g.id <= maxGroupId && g.assignedUser) {
          return g;
        }
        return false;
      });

      targetGroups = incr < 0 ? targetGroups.reverse() : targetGroups;

      const unchangedGroups = groups.filter((g) => {
        if (!(g.id >= minGroupId && g.id <= maxGroupId && g.assignedUser)) {
          return g;
        }
        return false;
      });

      // Reassign the users accordingly
      for (let i = 0; i < targetGroups.length; i++) {
        const g = targetGroups[i];
        if (g.id === toGroupId) {
          // assign the from user to the new 'to' group
          // this is the only explict option we need to cater for
          // since it is always a forward traversal of the array
          g.assignedUser = fromAssignedUser;
        }
        else {
          g.assignedUser = targetGroups[i + 1].assignedUser;
        }
      }

      // Re-sort the groups to be in the correct order
      const newGroups = _.concat(unchangedGroups, targetGroups)
        .sort((a, b) => a.id - b.id);

      return { ...state, groups: newGroups };
    }

    case types.CYCLIC_ORDER_SHIFT_USERS: {
      const allGroups = _.cloneDeep(state.groups);
      // Separate "New User" groups with and without assignedUser
      const newUserGroupsWithAssignedUser = allGroups.filter(group => group.title === 'New User' && group.assignedUser);
      const newUserGroupsWithoutAssignedUser = allGroups.filter(group => group.title === 'New User' && !group.assignedUser);
      // Filter out all "New User" groups
      const updatedGroups = allGroups.filter(group => group.title !== 'New User');
      // Retain "New User" groups with assignedUser in their original positions
      newUserGroupsWithAssignedUser.forEach((group) => {
          const index = allGroups.indexOf(group);
          updatedGroups.splice(index, 0, group);
      });
      if (updatedGroups.length === 0) {
          return state;
      }
      const lastGroup = updatedGroups[updatedGroups.length - 1];
      for (let i = updatedGroups.length - 1; i > 0; i--) {
          updatedGroups[i] = updatedGroups[i - 1];
      }
      updatedGroups[0] = lastGroup;
      /* eslint-disable no-param-reassign */
      updatedGroups.forEach((item, index) => {
          item.id = index;
      });
      /* eslint-disable no-param-reassign */
      updatedGroups.push(...newUserGroupsWithoutAssignedUser);
      return { ...state, groups: updatedGroups };
    }

  default:
    return state;
  }
};

